/**
 * 工具函数模块
 * 移植自 xhshow-go/xhshow/utils.go
 */

import {
  CUSTOM_BASE64_ALPHABET,
  X3_BASE64_ALPHABET,
  MAX_32BIT,
  HEX_CHARS,
  B3_TRACEID_LENGTH,
  XRAY_TRACEID_SEQ_MAX,
  XRAY_TRACEID_TIMESTAMP_SHIFT,
} from '../config.js';

/**
 * 自定义 Base64 编码
 */
export function encodeCustomBase64(data: Uint8Array): string {
  return encodeBase64WithAlphabet(data, CUSTOM_BASE64_ALPHABET);
}

/**
 * 自定义 Base64 解码
 */
export function decodeCustomBase64(data: string): Uint8Array {
  return decodeBase64WithAlphabet(data, CUSTOM_BASE64_ALPHABET);
}

/**
 * X3 Base64 编码
 */
export function encodeX3Base64(data: Uint8Array): string {
  return encodeBase64WithAlphabet(data, X3_BASE64_ALPHABET);
}

/**
 * X3 Base64 解码
 */
export function decodeX3Base64(data: string): Uint8Array {
  return decodeBase64WithAlphabet(data, X3_BASE64_ALPHABET);
}

/**
 * 使用自定义字母表进行 Base64 编码
 */
function encodeBase64WithAlphabet(data: Uint8Array, alphabet: string): string {
  const standardAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  // 先使用标准 Base64 编码
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  let base64 = btoa(binary);

  // 字符替换
  let result = '';
  for (let i = 0; i < base64.length; i++) {
    const char = base64[i];
    const idx = standardAlphabet.indexOf(char);
    if (idx !== -1) {
      result += alphabet[idx];
    } else {
      result += char; // 保留 '=' 填充字符
    }
  }
  return result;
}

/**
 * 使用自定义字母表进行 Base64 解码
 */
function decodeBase64WithAlphabet(data: string, alphabet: string): Uint8Array {
  const standardAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  // 字符还原
  let standardBase64 = '';
  for (let i = 0; i < data.length; i++) {
    const char = data[i];
    const idx = alphabet.indexOf(char);
    if (idx !== -1) {
      standardBase64 += standardAlphabet[idx];
    } else {
      standardBase64 += char; // 保留 '=' 填充字符
    }
  }

  // 使用标准 Base64 解码
  const binary = atob(standardBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 生成随机整数（0 到 MAX_32BIT）
 */
export function generateRandomInt(): number {
  return Math.floor(Math.random() * (MAX_32BIT + 1));
}

/**
 * 在范围内生成随机整数
 */
export function generateRandomByteInRange(minVal: number, maxVal: number): number {
  return Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
}

/**
 * 生成 B3 TraceId
 */
export function generateB3TraceId(): string {
  let result = '';
  for (let i = 0; i < B3_TRACEID_LENGTH; i++) {
    result += HEX_CHARS[Math.floor(Math.random() * HEX_CHARS.length)];
  }
  return result;
}

/**
 * 生成 Xray TraceId
 */
export function generateXrayTraceId(timestamp?: number, seq?: number): string {
  const ts = timestamp || Date.now();
  const s =
    seq !== undefined && seq !== -1 ? seq : Math.floor(Math.random() * (XRAY_TRACEID_SEQ_MAX + 1));

  // 计算 part1
  const part1Val = (BigInt(ts) << BigInt(XRAY_TRACEID_TIMESTAMP_SHIFT)) | BigInt(s);
  const part1 = part1Val.toString(16).padStart(16, '0');

  // 生成 part2
  let part2 = '';
  for (let i = 0; i < 16; i++) {
    part2 += HEX_CHARS[Math.floor(Math.random() * HEX_CHARS.length)];
  }

  return part1 + part2;
}

/**
 * 整数转小端字节数组
 */
export function intToLeBytes(val: number, length: number): number[] {
  const arr: number[] = [];
  for (let i = 0; i < length; i++) {
    arr.push(val & 0xff);
    val >>= 8;
  }
  return arr;
}

/**
 * 小端字节数组转整数
 */
export function leBytesToInt(bytes: number[]): number {
  let result = 0;
  for (let i = bytes.length - 1; i >= 0; i--) {
    result = (result << 8) | (bytes[i] & 0xff);
  }
  return result;
}

/**
 * 从 Buffer/Uint8Array 读取小端 uint32
 */
export function readUInt32LE(buffer: Uint8Array, offset: number): number {
  return (
    (buffer[offset] |
      (buffer[offset + 1] << 8) |
      (buffer[offset + 2] << 16) |
      (buffer[offset + 3] << 24)) >>>
    0
  );
}

/**
 * 从 Buffer/Uint8Array 读取小端 uint64（返回 BigInt）
 */
export function readUInt64LE(buffer: Uint8Array, offset: number): bigint {
  const low =
    buffer[offset] |
    (buffer[offset + 1] << 8) |
    (buffer[offset + 2] << 16) |
    (buffer[offset + 3] << 24);
  const high =
    buffer[offset + 4] |
    (buffer[offset + 5] << 8) |
    (buffer[offset + 6] << 16) |
    (buffer[offset + 7] << 24);
  return (BigInt(high) << 32n) | BigInt(low >>> 0);
}
